import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Circle, X } from "lucide-react";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const FLOW = ["Requested", "Scheduled", "Pickup Assigned", "Collected", "Weighed", "Accepted", "Points Credited"];

export default function TradeInStatus() {
  const { id } = useParams();
  const [t, setT] = useState(null);

  const load = () => api.get(`/tradein/${id}`).then(({ data }) => setT(data)).catch(() => setT(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (t === null) return <div className="min-h-[50vh] grid place-items-center text-muted-foreground">Loading…</div>;
  if (t === false) return <div className="min-h-[50vh] grid place-items-center">Trade-in not found.</div>;

  const rejected = t.status === "Rejected";
  const cancelled = t.status === "Cancelled";
  const currentIdx = FLOW.indexOf(t.status);

  const cancel = async () => {
    try { await api.post(`/tradein/${id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot cancel"); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
      <Link to="/account" className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 mb-6"><ArrowLeft size={16} /> Back to account</Link>
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="font-heading font-extrabold text-2xl">Trade-In <span className="font-mono-points">#{t.trade_id}</span></h1>
          <span className={`text-sm font-semibold px-3 py-1.5 rounded-md ${rejected || cancelled ? "bg-destructive text-white" : t.status === "Points Credited" ? "bg-[hsl(var(--secondary))] text-white" : "bg-primary/15 text-primary"}`} data-testid="ti-status">{t.status}</span>
        </div>
        <div className="mt-2 text-muted-foreground text-sm">
          {t.actual_weight ? `${t.actual_weight} kg actual weight` : `~${t.estimated_kg} kg estimated`} · pickup {t.pickup_date} ({t.pickup_time})
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <Info label="Estimated reward" value={money(t.estimated_points)} />
          <Info label="RicePoints credited" value={t.points_credited ? money(t.points_credited) : "—"} accent />
        </div>

        {rejected && (
          <div className="mt-5 bg-destructive/10 text-destructive rounded-lg p-4 text-sm">
            <b>Rejected:</b> {t.rejection_reason}
          </div>
        )}

        {!rejected && !cancelled && (
          <div className="mt-8">
            <div className="font-heading font-bold mb-4">Status timeline</div>
            <ol className="space-y-0">
              {FLOW.map((s, i) => {
                const doneStep = i <= currentIdx;
                return (
                  <li key={s} className="flex items-start gap-3 pb-6 last:pb-0 relative">
                    {i < FLOW.length - 1 && <span className={`absolute left-[11px] top-6 bottom-0 w-0.5 ${i < currentIdx ? "bg-[hsl(var(--secondary))]" : "bg-border"}`} />}
                    <span className={`grid place-items-center w-6 h-6 rounded-full shrink-0 z-10 ${doneStep ? "bg-[hsl(var(--secondary))] text-white" : "bg-muted text-muted-foreground"}`}>
                      {doneStep ? <Check size={13} /> : <Circle size={9} />}
                    </span>
                    <span className={`text-sm ${doneStep ? "font-semibold" : "text-muted-foreground"}`}>{s}</span>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {!["Points Credited", "Rejected", "Cancelled"].includes(t.status) && (
          <Button variant="outline" className="mt-4 text-destructive" onClick={cancel} data-testid="ti-cancel-btn"><X size={16} /> Cancel request</Button>
        )}
      </div>
    </div>
  );
}

function Info({ label, value, accent }) {
  return (
    <div className="bg-muted rounded-lg p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-heading font-bold text-xl font-mono-points ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}
